import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cartService } from '../services/cartService';
import { BsTrash, BsX, BsCart3, BsDash, BsPlus } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

const SideCart = ({ isOpen, onClose, autoCloseDelay = 5000 }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [updatingQuantity, setUpdatingQuantity] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Check if current language is Arabic
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      fetchCart();
      
      // Show success message when cart opens and hide it after 3 seconds
      setShowSuccessMessage(true);
      const successTimer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000); // Hide after 3 seconds
      
      // Auto-close after delay if specified
      if (autoCloseDelay > 0) {
        const timer = setTimeout(() => {
          onClose();
        }, autoCloseDelay);
        
        return () => {
          clearTimeout(timer);
          clearTimeout(successTimer);
        };
      }
      
      return () => clearTimeout(successTimer);
    } else {
      // Delay removing from DOM to allow exit animation
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setShowSuccessMessage(false); // Reset success message when closing
      }, 300); // Match the animation duration
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoCloseDelay, onClose]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      setError(null);
      const cartData = await cartService.getCart();
      setCart(cartData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (productId) => {
    try {
      await cartService.removeFromCart(productId);
      await fetchCart(); // Refresh cart
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(productId);
      return;
    }

    try {
      setUpdatingQuantity(prev => ({ ...prev, [productId]: true }));
      await cartService.addToCart(productId, newQuantity);
      await fetchCart(); // Refresh cart
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingQuantity(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleIncreaseQuantity = (productId, currentQuantity) => {
    handleUpdateQuantity(productId, currentQuantity + 1);
  };

  const handleDecreaseQuantity = (productId, currentQuantity) => {
    handleUpdateQuantity(productId, currentQuantity - 1);
  };

  const handleProceedToOrder = () => {
    if (cart && cart.items.length > 0) {
      // Navigate to checkout page with cart data
      navigate('/checkout', { state: { cart } });
      onClose();
    }
  };

  const handleBackToProducts = () => {
    navigate('/products');
  };
  // Don't render if not open and not animating
  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* Backdrop with fade animation */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 999,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
        onClick={onClose}
      />

      {/* Side Cart with enhanced sliding animation */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          [isRTL ? 'left' : 'right']: 0,
          height: '100vh',
          width: window.innerWidth <= 768 ? '100vw' : '400px', // Full width on mobile
          maxWidth: window.innerWidth <= 768 ? '100vw' : '90vw',
          backgroundColor: 'white',
          boxShadow: isOpen 
            ? (isRTL ? '10px 0 30px rgba(0, 0, 0, 0.2)' : '-10px 0 30px rgba(0, 0, 0, 0.2)')
            : (isRTL ? '4px 0 20px rgba(0, 0, 0, 0.15)' : '-4px 0 20px rgba(0, 0, 0, 0.15)'),
          zIndex: 1000,
          transform: isOpen 
            ? 'translateX(0)' 
            : (isRTL ? 'translateX(-100%)' : 'translateX(100%)'),
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          direction: isRTL ? 'rtl' : 'ltr',
          // Add a subtle border for depth
          [isRTL ? 'borderRight' : 'borderLeft']: window.innerWidth <= 768 ? 'none' : '1px solid #e0e0e0'
        }}
      >
        {/* Header with slide-in animation */}
        <div
          style={{
            padding: window.innerWidth <= 768 ? '15px' : '20px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            transform: isOpen ? 'translateY(0)' : 'translateY(-20px)',
            opacity: isOpen ? 1 : 0,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
            direction: isRTL ? 'rtl' : 'ltr'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: window.innerWidth <= 768 ? '35px' : '40px',
                height: window.innerWidth <= 768 ? '35px' : '40px',
                borderRadius: '50%',
                backgroundColor: '#9143DA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: isOpen ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-10deg)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
              }}
            >
              <BsCart3 size={window.innerWidth <= 768 ? 16 : 18} color="white" />
            </div>
            <h3 
              style={{ 
                margin: 0, 
                color: '#2c3e50', 
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                transform: isOpen ? 'translateX(0)' : (isRTL ? 'translateX(10px)' : 'translateX(-10px)'),
                opacity: isOpen ? 1 : 0,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
              }}
            >
              {t('Shopping Cart')}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: window.innerWidth <= 768 ? '20px' : '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
              transform: isOpen ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(90deg)',
              opacity: isOpen ? 1 : 0
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f0f0';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <BsX />
          </button>
        </div>

        {/* Content with staggered animation */}
        <div 
          style={{ 
            flex: 1, 
            overflow: 'auto', 
            padding: window.innerWidth <= 768 ? '15px' : '20px',
            transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
            opacity: isOpen ? 1 : 0,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
            direction: isRTL ? 'rtl' : 'ltr'
          }}
        >
          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #9143DA',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite, pulse 2s ease-in-out infinite',
                  margin: '0 auto 10px',
                }}
              />
              <p style={{ color: '#666' }}>{t('Loading cart...')}</p>
            </div>
          )}

          {error && (
            <div
              style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '8px',
                border: '1px solid #f5c6cb',
                transform: isOpen ? 'scale(1)' : 'scale(0.95)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
              }}
            >
              <p>{t('Error')}: {error}</p>
            </div>
          )}

          {cart && !loading && (
            <>
              {cart.items.length === 0 ? (
                <div 
                  style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                    opacity: isOpen ? 1 : 0,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.3s'
                  }}
                >
                  <div
                    style={{
                      transform: isOpen ? 'scale(1)' : 'scale(0.8)',
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.4s'
                    }}
                  >
                    <BsCart3 size={48} color="#ddd" style={{ marginBottom: '15px' }} />
                    <p style={{ color: '#666', fontSize: '16px' }}>{t('Your cart is empty')}</p>
                    <p style={{ color: '#999', fontSize: '14px' }}>
                      {t('Add some products to get started!')}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Success Message with auto-hide */}
                  {showSuccessMessage && (
                    <div
                      style={{
                        backgroundColor: '#d4edda',
                        color: '#155724',
                        padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #c3e6cb',
                        marginBottom: '15px',
                        fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transform: isOpen && showSuccessMessage ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)',
                        opacity: isOpen && showSuccessMessage ? 1 : 0,
                        transition: 'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}
                    >
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                        style={{
                          transform: isOpen && showSuccessMessage ? 'scale(1)' : 'scale(0)',
                          transition: 'transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.4s'
                        }}
                      >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      {t('Product added successfully!')}
                    </div>
                  )}

                  {/* Cart Items with enhanced quantity controls */}
                  <div style={{ marginBottom: '15px' }}>
                    {cart.items.map((item, index) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: window.innerWidth <= 768 ? '12px' : '15px',
                          border: '1px solid #eee',
                          borderRadius: '8px',
                          marginBottom: '10px',
                          backgroundColor: '#fafafa',
                          transform: isOpen ? 'translateX(0)' : (isRTL ? 'translateX(-20px)' : 'translateX(20px)'),
                          opacity: isOpen ? 1 : 0,
                          transition: `all 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${0.3 + index * 0.1}s`,
                          direction: isRTL ? 'rtl' : 'ltr',
                          flexWrap: window.innerWidth <= 480 ? 'wrap' : 'nowrap'
                        }}
                      >
                        {/* Product Image */}
                        <div style={{ 
                          width: window.innerWidth <= 768 ? '40px' : '50px', 
                          height: window.innerWidth <= 768 ? '40px' : '50px', 
                          [isRTL ? 'marginLeft' : 'marginRight']: window.innerWidth <= 768 ? '8px' : '12px',
                          flexShrink: 0
                        }}>
                          {item.image_id ? (
                            <img
                              src={`https://file.talabatn.net/${item.image_id}`}
                              alt={item.product_name}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                transition: 'transform 0.2s ease'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#e9ecef',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#6c757d',
                                fontSize: '8px',
                              }}
                            >
                              {t('No Image')}
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div style={{ 
                          flex: 1, 
                          [isRTL ? 'marginLeft' : 'marginRight']: window.innerWidth <= 768 ? '8px' : '12px',
                          textAlign: isRTL ? 'right' : 'left',
                          minWidth: 0,
                          width: window.innerWidth <= 480 ? '100%' : 'auto'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 2px 0', 
                            fontSize: window.innerWidth <= 768 ? '12px' : '14px', 
                            fontWeight: '600',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.product_name}
                          </h5>
                          <p style={{ 
                            margin: '2px 0 0 0', 
                            fontWeight: '600', 
                            color: '#9143DA', 
                            fontSize: window.innerWidth <= 768 ? '12px' : '14px' 
                          }}>
                            {item.subtotal.toFixed(2)} {t('TND')}
                          </p>
                          {item.discount_percentage > 0 && (
                            <p style={{ 
                              margin: '1px 0 0 0', 
                              fontSize: window.innerWidth <= 768 ? '9px' : '11px', 
                              color: '#dc3545' 
                            }}>
                              -{item.discount_percentage}% {t('discount')}
                            </p>
                          )}
                        </div>

                        {/* Quantity Controls and Remove Button Container */}
                        <div style={{
                          display: 'flex',
                          flexDirection: window.innerWidth <= 480 ? 'row' : 'column',
                          alignItems: 'center',
                          gap: window.innerWidth <= 480 ? '8px' : '6px',
                          flexShrink: 0,
                          width: window.innerWidth <= 480 ? '100%' : 'auto',
                          justifyContent: window.innerWidth <= 480 ? 'space-between' : 'center',
                          marginTop: window.innerWidth <= 480 ? '8px' : '0'
                        }}>
                          {/* Quantity Controls */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '15px',
                            padding: '2px',
                            border: '1px solid #e9ecef',
                            direction: 'ltr'
                          }}>
                            {/* Decrease Button */}
                            <button
                              onClick={() => handleDecreaseQuantity(item.product_id, item.quantity)}
                              disabled={updatingQuantity[item.product_id] || item.quantity <= 1}
                              style={{
                                width: window.innerWidth <= 768 ? '20px' : '24px',
                                height: window.innerWidth <= 768 ? '20px' : '24px',
                                borderRadius: '50%',
                                border: 'none',
                                backgroundColor: updatingQuantity[item.product_id] || item.quantity <= 1 ? '#e9ecef' : '#9143DA',
                                color: updatingQuantity[item.product_id] || item.quantity <= 1 ? '#6c757d' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: updatingQuantity[item.product_id] || item.quantity <= 1 ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '10px'
                              }}
                            >
                              <BsDash size={window.innerWidth <= 768 ? 8 : 10} />
                            </button>

                            {/* Quantity Display */}
                            <span style={{
                              minWidth: window.innerWidth <= 768 ? '25px' : '30px',
                              textAlign: 'center',
                              fontSize: window.innerWidth <= 768 ? '11px' : '13px',
                              fontWeight: '600',
                              color: '#2c3e50',
                              padding: window.innerWidth <= 768 ? '0 4px' : '0 8px'
                            }}>
                              {updatingQuantity[item.product_id] ? (
                                <div
                                  style={{
                                    width: '10px',
                                    height: '10px',
                                    border: '2px solid #f3f3f3',
                                    borderTop: '2px solid #9143DA',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    margin: '0 auto'
                                  }}
                                />
                              ) : (
                                item.quantity
                              )}
                            </span>

                            {/* Increase Button */}
                            <button
                              onClick={() => handleIncreaseQuantity(item.product_id, item.quantity)}
                              disabled={updatingQuantity[item.product_id]}
                              style={{
                                width: window.innerWidth <= 768 ? '20px' : '24px',
                                height: window.innerWidth <= 768 ? '20px' : '24px',
                                borderRadius: '50%',
                                border: 'none',
                                backgroundColor: updatingQuantity[item.product_id] ? '#e9ecef' : '#9143DA',
                                color: updatingQuantity[item.product_id] ? '#6c757d' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: updatingQuantity[item.product_id] ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '10px'
                              }}
                            >
                              <BsPlus size={window.innerWidth <= 768 ? 8 : 10} />
                            </button>
                          </div>

                          {/* Unit Price Display */}
                          {window.innerWidth > 480 && (
                            <div style={{ 
                              fontSize: window.innerWidth <= 768 ? '9px' : '10px', 
                              color: '#666', 
                              textAlign: 'center' 
                            }}>
                              {item.discounted_price.toFixed(2)} {t('TND')}/unit
                            </div>
                          )}

                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveItem(item.product_id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc3545',
                              cursor: 'pointer',
                              padding: window.innerWidth <= 768 ? '6px' : '8px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              transform: 'scale(1)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f8d7da';
                              e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            <BsTrash size={window.innerWidth <= 768 ? 12 : 14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Summary with slide-up animation */}
                  <div
                    style={{
                      backgroundColor: '#f8f9fa',
                      padding: window.innerWidth <= 768 ? '12px' : '16px',
                      borderRadius: '8px',
                      marginBottom: window.innerWidth <= 768 ? '15px' : '20px',
                      border: '1px solid #e9ecef',
                      transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                      opacity: isOpen ? 1 : 0,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.5s',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '6px', 
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      <span>{t('Subtotal')}:</span>
                      <span>{cart.subtotal.toFixed(2)} {t('TND')}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '6px', 
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      <span>{t('Shipping')}:</span>
                      <span>{cart.shipping_fee.toFixed(2)} {t('TND')}</span>
                    </div>
                    <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontWeight: 'bold', 
                      fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      <span>{t('Total')}:</span>
                      <span style={{ color: '#9143DA' }}>{cart.total.toFixed(2)} {t('TND')}</span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer Actions with slide-up animation */}
        {cart && cart.items.length > 0 && (
          <div
            style={{
              padding: window.innerWidth <= 768 ? '15px' : '20px',
              borderTop: '1px solid #eee',
              backgroundColor: '#f8f9fa',
              transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
              opacity: isOpen ? 1 : 0,
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.6s',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
          >
            <div style={{ 
              display: 'flex', 
              gap: window.innerWidth <= 768 ? '8px' : '10px',
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row'
            }}>
              <button
                onClick={()=> {handleBackToProducts(); onClose();}}
                style={{
                  flex: 1,
                  padding: window.innerWidth <= 768 ? '10px' : '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.borderColor = '#9143DA';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#ddd';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                {t('Continue Shopping')}
              </button>
              <button
                onClick={handleProceedToOrder}
                style={{
                  flex: 1,
                  padding: window.innerWidth <= 768 ? '10px' : '12px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#9143DA',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 8px rgba(145, 67, 218, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#7c2fb8';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(145, 67, 218, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#9143DA';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(145, 67, 218, 0.3)';
                }}
              >
                {t('Proceed to Order')}
              </button>
            </div>
          </div>
        )}

        {/* Enhanced CSS Animations */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
            
            @keyframes slideInRight {
              0% {
                transform: translateX(100%);
                opacity: 0;
              }
              100% {
                transform: translateX(0);
                opacity: 1;
              }
            }
            
            @keyframes slideOutRight {
              0% {
                transform: translateX(0);
                opacity: 1;
              }
              100% {
                transform: translateX(100%);
                opacity: 0;
              }
            }
            
            @keyframes bounceIn {
              0% {
                transform: scale(0.3);
                opacity: 0;
              }
              50% {
                transform: scale(1.05);
              }
              70% {
                transform: scale(0.9);
              }
              100% {
                transform: scale(1);
                opacity: 1;
              }
            }
            
            @keyframes fadeOut {
              0% {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
              100% {
                opacity: 0;
                transform: translateY(-10px) scale(0.95);
              }
            }
          `}
        </style>
      </div>
    </>
  );
};

export default SideCart;
